/**
 * Mirrors the worker's `selectClipWords` rule exactly:
 * - word text = `punctuated_word || word` trimmed (this frontend also
 *   accepts a `text` alias, matching `buildClipTranscriptionSegments.ts`)
 * - drop words without text or finite start/end
 * - keep a word when its midpoint `(start+end)/2` lies inside any valid
 *   segment `{start,end}` (`end > start`)
 * - no valid segments -> keep all words
 * - re-index from 0
 */

export interface TranscriptWord {
  index: number;
  text: string;
  start: number;
  end: number;
}

interface RawWordLike {
  word?: unknown;
  punctuated_word?: unknown;
  text?: unknown;
  start?: unknown;
  end?: unknown;
}

interface RawSegmentLike {
  start?: unknown;
  end?: unknown;
}

interface ValidSegment {
  start: number;
  end: number;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function normalizeWordText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function extractWordText(raw: RawWordLike): string {
  // Mirrors the worker's `word?.punctuated_word || word?.word` fallback:
  // an empty/whitespace-only string at any step is treated as absent, so it
  // falls through to the next alias rather than dropping the word.
  return (
    normalizeWordText(raw.punctuated_word)
    || normalizeWordText(raw.word)
    || normalizeWordText(raw.text)
  );
}

function extractValidSegments(segments: unknown): ValidSegment[] {
  if (!Array.isArray(segments)) return [];

  const valid: ValidSegment[] = [];
  for (const raw of segments) {
    if (raw === null || typeof raw !== 'object') continue;
    const { start, end } = raw as RawSegmentLike;
    if (isFiniteNumber(start) && isFiniteNumber(end) && end > start) {
      valid.push({ start, end });
    }
  }
  return valid;
}

export function selectTranscriptWords(words: unknown, segments: unknown): TranscriptWord[] {
  if (!Array.isArray(words)) return [];

  const validSegments = extractValidSegments(segments);

  const candidates: { text: string; start: number; end: number }[] = [];
  for (const raw of words) {
    if (raw === null || typeof raw !== 'object') continue;
    const rawWord = raw as RawWordLike;
    const text = extractWordText(rawWord);
    const { start, end } = rawWord;
    if (!text || !isFiniteNumber(start) || !isFiniteNumber(end)) continue;
    candidates.push({ text, start, end });
  }

  const kept = validSegments.length === 0
    ? candidates
    : candidates.filter((word) => {
      const midpoint = (word.start + word.end) / 2;
      return validSegments.some((segment) => midpoint >= segment.start && midpoint <= segment.end);
    });

  return kept.map((word, index) => ({ index, ...word }));
}

/**
 * Joins the text of every word whose `index` lies within `[from, to]`
 * (inclusive) — used to reconstruct the quote text from an LLM-matched span.
 */
export function wordsToQuoteText(words: TranscriptWord[], from: number, to: number): string {
  return words
    .filter((word) => word.index >= from && word.index <= to)
    .map((word) => word.text)
    .join(' ')
    .trim();
}
